/**
 * Coordinate transform between world (physics) coordinates and canvas (screen) coordinates.
 *
 * World frame (physics engine):
 *   X: horizontal, right positive (downrange), meters
 *   Z: vertical, up positive (altitude), meters, ground = 0
 *
 * Canvas frame:
 *   x: horizontal, right positive, pixels
 *   y: vertical, down positive, pixels
 *
 * The transform maps a world rectangle to the canvas while preserving aspect ratio.
 * Ground (Z=0) is mapped to a fixed offset from the bottom of the canvas.
 * X=0 is mapped to the horizontal center of the canvas.
 */

export interface ViewportConfig {
  /** World X range half-width. The visible world spans [-worldX, +worldX]. */
  worldX: number;
  /** World Z max altitude. The visible world spans [0, worldZ]. */
  worldZ: number;
  /** Canvas width in pixels. */
  canvasWidth: number;
  /** Canvas height in pixels. */
  canvasHeight: number;
  /** Ground offset from bottom of canvas in pixels. */
  groundOffset: number;
  /** Scale mode: 'fit' preserves aspect ratio; 'stretch' fills canvas. */
  scaleMode: "fit" | "stretch";
}

export interface Transform {
  /** World-to-canvas X. */
  wx: (x: number) => number;
  /** World-to-canvas Z (altitude -> canvas Y). */
  wz: (z: number) => number;
  /** Canvas-to-world X. */
  cx: (px: number) => number;
  /** Canvas-to-world Z. */
  cz: (py: number) => number;
  /** Pixels per meter (uniform scale). */
  scale: number;
  /** Ground line canvas Y. */
  groundY: number;
  /** Center X in canvas pixels (world X=0). */
  centerX: number;
}

/**
 * Build a coordinate transform for the given viewport config.
 */
export function makeTransform(config: ViewportConfig): Transform {
  const { canvasWidth, canvasHeight, groundOffset, scaleMode } = config;

  // Available drawing area below the top margin and above the ground offset.
  const topMargin = 40;
  const bottomMargin = groundOffset;
  const availableWidth = canvasWidth - 40; // left/right margins
  const availableHeight = canvasHeight - topMargin - bottomMargin;

  let scaleX: number;
  let scaleY: number;

  if (scaleMode === "fit") {
    // Preserve aspect ratio: fit the world box into the available area.
    scaleX = availableWidth / (2 * config.worldX);
    scaleY = availableHeight / config.worldZ;
    const scale = Math.min(scaleX, scaleY);
    scaleX = scale;
    scaleY = scale;
  } else {
    scaleX = availableWidth / (2 * config.worldX);
    scaleY = availableHeight / config.worldZ;
  }

  const centerX = canvasWidth / 2;
  const groundY = canvasHeight - bottomMargin;

  return {
    wx: (x: number) => centerX + x * scaleX,
    wz: (z: number) => groundY - z * scaleY,
    cx: (px: number) => (px - centerX) / scaleX,
    cz: (py: number) => (groundY - py) / scaleY,
    scale: Math.min(scaleX, scaleY),
    groundY,
    centerX,
  };
}

/**
 * Compute a ViewportConfig that fits the given trajectory bounds with padding.
 */
export function computeViewportConfig(
  points: { x: number; z: number }[],
  canvasWidth: number,
  canvasHeight: number,
  groundOffset: number = 60,
): ViewportConfig {
  let minX = 0;
  let maxX = 0;
  let maxZ = 0;

  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    maxZ = Math.max(maxZ, p.z);
  }

  // Add padding.
  const padX = Math.max(50, (maxX - minX) * 0.15);
  const padZ = Math.max(30, maxZ * 0.1);

  const worldX = Math.max(Math.abs(minX), Math.abs(maxX)) + padX;
  const worldZ = Math.max(maxZ + padZ, 100); // minimum visible altitude

  return {
    worldX,
    worldZ,
    canvasWidth,
    canvasHeight,
    groundOffset,
    scaleMode: "fit",
  };
}
